import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Gauge, Plus, MessageSquare, Shield, LogOut, User as UserIcon, Store, BadgeCheck, UploadCloud } from "lucide-react";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem,
  DropdownMenuTrigger, DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";

export const Navbar = () => {
  const { user, login, logout } = useAuth();
  const navigate = useNavigate();

  return (
    <header className="sticky top-0 z-50 bg-[#0A0A0A]/90 backdrop-blur-md border-b border-border">
      <div className="max-w-[1500px] mx-auto px-4 sm:px-6 h-16 flex items-center justify-between">
        <Link to="/" data-testid="nav-logo" className="flex items-center gap-2 group">
          <div className="w-9 h-9 bg-blaze flex items-center justify-center rounded-sm group-hover:rotate-90 transition-transform duration-300">
            <Gauge className="w-5 h-5 text-white" strokeWidth={2.5} />
          </div>
          <span className="font-heading font-900 text-xl tracking-tight uppercase">Fit<span className="text-blaze">Wise</span></span>
        </Link>

        <nav className="flex items-center gap-1 sm:gap-2">
          <Link to="/search" data-testid="nav-search">
            <Button variant="ghost" className="text-sm font-semibold hover:text-blaze hover:bg-transparent">Browse</Button>
          </Link>
          {user ? (
            <>
              <Button
                data-testid="nav-sell-btn"
                onClick={() => navigate("/sell")}
                className="bg-blaze hover:bg-blaze-hover text-white rounded-sm font-bold gap-1.5 hover:-translate-y-0.5 transition-transform"
              >
                <Plus className="w-4 h-4" /> Sell
              </Button>
              <Link to="/messages" data-testid="nav-messages">
                <Button variant="ghost" size="icon" className="hover:text-blaze hover:bg-secondary">
                  <MessageSquare className="w-5 h-5" />
                </Button>
              </Link>
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <button data-testid="nav-avatar" className="ml-1 outline-none">
                    <Avatar className="w-9 h-9 border border-border">
                      <AvatarImage src={user.picture} />
                      <AvatarFallback className="bg-secondary text-xs">{user.name?.[0]?.toUpperCase()}</AvatarFallback>
                    </Avatar>
                  </button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="bg-[#141414] border-border w-52">
                  <div className="px-2 py-1.5 text-xs text-muted-foreground truncate">{user.email}</div>
                  <DropdownMenuSeparator className="bg-border" />
                  <DropdownMenuItem data-testid="menu-dashboard" onClick={() => navigate("/dashboard")} className="gap-2 cursor-pointer focus:bg-secondary">
                    <Gauge className="w-4 h-4" /> Dashboard
                  </DropdownMenuItem>
                  <DropdownMenuItem data-testid="menu-profile" onClick={() => navigate(`/seller/${user.user_id}`)} className="gap-2 cursor-pointer focus:bg-secondary">
                    <UserIcon className="w-4 h-4" /> My Profile
                  </DropdownMenuItem>
                  {user.is_dealer ? (
                    <>
                      <DropdownMenuItem data-testid="menu-dealer" onClick={() => navigate("/dealer")} className="gap-2 cursor-pointer focus:bg-secondary text-blaze">
                        <BadgeCheck className="w-4 h-4" /> Verified Dealer
                      </DropdownMenuItem>
                      <DropdownMenuItem data-testid="menu-import" onClick={() => navigate("/dealer/import")} className="gap-2 cursor-pointer focus:bg-secondary">
                        <UploadCloud className="w-4 h-4" /> Import Inventory
                      </DropdownMenuItem>
                    </>
                  ) : (
                    <DropdownMenuItem data-testid="menu-become-dealer" onClick={() => navigate("/dealer")} className="gap-2 cursor-pointer focus:bg-secondary">
                      <Store className="w-4 h-4" /> Become a Dealer
                    </DropdownMenuItem>
                  )}
                  {user.role === "admin" && (
                    <DropdownMenuItem data-testid="menu-admin" onClick={() => navigate("/admin")} className="gap-2 cursor-pointer focus:bg-secondary text-blaze">
                      <Shield className="w-4 h-4" /> Admin
                    </DropdownMenuItem>
                  )}
                  <DropdownMenuSeparator className="bg-border" />
                  <DropdownMenuItem data-testid="menu-logout" onClick={logout} className="gap-2 cursor-pointer focus:bg-secondary text-destructive">
                    <LogOut className="w-4 h-4" /> Logout
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </>
          ) : (
            <Button data-testid="nav-login-btn" onClick={login} className="bg-blaze hover:bg-blaze-hover text-white rounded-sm font-bold hover:-translate-y-0.5 transition-transform">
              Sign In
            </Button>
          )}
        </nav>
      </div>
    </header>
  );
};
